import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

interface SeriesSuggestionModalProps {
    isOpen: boolean;
    onClose: () => void;
    recapId: number;
    currentSeriesName?: string;
    onSuggestionSubmitted?: () => void;
}

const SeriesSuggestionModal: React.FC<SeriesSuggestionModalProps> = ({
    isOpen,
    onClose,
    recapId,
    currentSeriesName,
    onSuggestionSubmitted
}) => {
    const [suggestedName, setSuggestedName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dailyRemaining, setDailyRemaining] = useState<number | null>(null);
    const { user } = useAuth();

    // Sanitize input as user types
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;

        // Remove special characters except common ones
        value = value.replace(/[^\w\s\-'&.,!?()]/g, '');

        // Remove non-alphanumeric from start
        value = value.replace(/^[^a-zA-Z0-9]+/, '');

        setSuggestedName(value);
    };

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!suggestedName.trim()) {
            setError('Please enter a series name');
            return;
        }

        if (!user) {
            setError('You must be logged in to submit suggestions');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch('/api/suggestions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    recap_id: recapId,
                    suggested_series_name: suggestedName.trim()
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess('Thanks! We\'ll review your suggestion soon.');
                setDailyRemaining(data.daily_remaining);
                setSuggestedName('');
                setTimeout(() => {
                    onClose();
                    onSuggestionSubmitted?.();
                }, 2000);
            } else {
                setError(data.error || 'Failed to submit suggestion');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
            onClick={handleBackdropClick}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md"
                onClick={handleModalClick}
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Suggest Series Name
                    </h2>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        ✕
                    </button>
                </div>

                {currentSeriesName && (
                    <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Current series name: <span className="font-medium">{currentSeriesName}</span>
                        </p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="seriesName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Series Name
                        </label>
                        <input
                            type="text"
                            id="seriesName"
                            value={suggestedName}
                            onChange={handleInputChange}
                            maxLength={100}
                            placeholder="Enter series name..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            disabled={isSubmitting}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            {suggestedName.length}/100 characters
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 rounded">
                            {success}
                            {dailyRemaining !== null && (
                                <p className="text-xs mt-1">
                                    You have {dailyRemaining} suggestions remaining today.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !suggestedName.trim()}
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Suggestion'}
                        </button>
                    </div>
                </form>

                <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    <p>• Your suggestion will be reviewed by our team</p>
                    <p>• You can only suggest one name per recap</p>
                    <p>• You can submit up to 10 suggestions per day</p>
                    <p>• Approved suggestions will update the series name</p>
                </div>
            </div>
        </div>
    );
};

export default SeriesSuggestionModal; 